import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Bed, Bathtub, UsersThree, MapPin, ArrowUpRight, Star, Heart } from "@phosphor-icons/react";
import { trackBookingIntent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Live "from" rates — fetched once per page load, shared by all cards.
let ratesPromise = null;
const loadRates = () => (ratesPromise ??= fetch(`${API}/rates`).then((r) => (r.ok ? r.json() : {})).catch(() => ({})));

const fmtRate = (rate) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: rate.currency || "USD", maximumFractionDigits: 0 }).format(rate.amount / 100);

export default function PropertyCard({ property, index = 0, wide = false }) {
  // Carry any selected dates (from the events calendar / booking search) through to the detail page.
  const { search } = useLocation();
  const dateQ = (() => {
    const p = new URLSearchParams(search);
    const ci = p.get("checkin");
    const co = p.get("checkout");
    return ci && co ? `?checkin=${ci}&checkout=${co}` : "";
  })();

  const [rate, setRate] = useState(null);
  useEffect(() => {
    let live = true;
    loadRates().then((rates) => {
      if (live) setRate(rates[property.slug] || null);
    });
    return () => { live = false; };
  }, [property.slug]);
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative ${wide ? "md:col-span-2" : ""}`}
      data-testid={`property-card-${property.slug}`}
    >
      <Link to={`/properties/${property.slug}${dateQ}`} className="block">
        <div className={`relative overflow-hidden rounded-2xl ${wide ? "aspect-[16/10] md:aspect-[21/10]" : "aspect-[4/5]"}`}>
          <img
            src={property.image}
            alt={property.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-emerald/70 via-emerald/10 to-transparent" />
          <div className="absolute top-4 left-4 flex flex-col items-start gap-2">
            {property.guestFavourite && (
              <span data-testid={`guest-favourite-${property.slug}`} className="flex items-center gap-1.5 rounded-full bg-sunset px-3 py-1.5 text-xs font-semibold text-sand shadow-lg">
                <Heart size={13} weight="fill" /> Guest favorite
              </span>
            )}
            <span className="flex items-center gap-1.5 rounded-full bg-sand/90 backdrop-blur px-3 py-1.5 text-xs text-emerald">
              <MapPin size={13} weight="fill" className="text-sunset" /> {property.location}
            </span>
          </div>
          {property.rating && (
            <div data-testid={`rating-${property.slug}`} className="absolute top-4 right-4 flex items-center gap-1.5 rounded-full bg-sand/90 backdrop-blur px-3 py-1.5 text-xs font-semibold text-emerald group-hover:opacity-0 transition-opacity duration-300">
              <Star size={13} weight="fill" className="text-gold" /> {property.rating.toFixed(1)}
            </div>
          )}
          <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-sunset grid place-items-center opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-400">
            <ArrowUpRight size={18} className="text-sand" weight="bold" />
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-6 text-sand">
            <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 max-w-md">{property.name}</h3>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-sand/90">
              {property.rating && (
                <span data-testid={`rating-badge-${property.slug}`} className="flex items-center gap-1.5 font-medium">
                  <Star size={15} weight="fill" className="text-gold" /> {property.rating.toFixed(1)}
                </span>
              )}
              <span className="flex items-center gap-1.5"><UsersThree size={16} /> {property.guests} guests</span>
              <span className="flex items-center gap-1.5"><Bed size={16} /> {property.bedrooms} bed{property.bedrooms > 1 ? "s" : ""}</span>
              <span className="flex items-center gap-1.5"><Bathtub size={16} /> {property.bathrooms} bath{property.bathrooms > 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>
      </Link>

      {/* Availability peek — deep-links straight to this property's live booking widget */}
      <Link
        to={`/properties/${property.slug}${dateQ}#book`}
        data-testid={`availability-peek-${property.slug}`}
        aria-label={`Check live rates and availability for ${property.name}`}
        onClick={() => trackBookingIntent(property)}
        className="absolute bottom-5 right-5 z-10 flex items-center gap-2 rounded-full bg-sand/90 backdrop-blur px-3.5 py-2 text-[11px] font-medium text-emerald shadow-lg transition-colors duration-300 hover:bg-sunset hover:text-sand"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-sage opacity-75 animate-ping" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-sage" />
        </span>
        <span data-testid={`availability-rate-${property.slug}`} className="whitespace-nowrap">
          {rate ? <>From {fmtRate(rate)}/night</> : "Check live rates"}
        </span>
      </Link>
    </motion.div>
  );
}
